/*
 * Project Page Panel Template
 *
 * Includes:
 * - Project Page
 * - Asset Viewer Page
 */
import type {
  PanelArea,
  PanelsBreakpointTemplate,
  PanelsTemplate,
  PanelUnitProps,
  ViewerPanelArea,
} from '../types';
import {
  AI_DEFAULT_MIN_WIDTH,
  AI_DEFAULT_WIDTH,
  DETAILS_DEFAULT_WIDTH,
  DETAILS_ROW_MIN_HEIGHT_BASE,
  LARGE_NAV_DEFAULT_WIDTH,
  MEDIUM_NAV_DEFAULT_WIDTH,
  MEDIUM_NAV_MAX_WIDTH,
  PROJECT_NAV_MIN_WIDTH,
  VIEWER_ROW_MIN_HEIGHT_BASE,
} from './constants';
import TEXT from './generated/TEXT/viewer';

TEXT.DEFINE({
  details: {
    accessibility: 'Toggle the asset Info',
    hide: 'Hide Asset Info',
    show: 'Show Asset Info',
  },
  /** AI panel toggle labels */
  ai: {
    /** Aria label for toggling AI panel */
    accessibility: 'Toggle the AI Panel',
    /** Label when hiding AI panel */
    hide: 'Hide AI Panel',
    /** Label when showing AI panel */
    show: 'Show AI Panel',
  },
});

/**
 * Base template (Mobile +)
 */
export function baseTemplate(
  visible: Array<PanelArea>
): PanelsBreakpointTemplate {
  return {
    areas: [
      ['directoryNav', 'directoryContent', 'viewer', 'ai'],
      ['details', 'details', 'details', 'ai'],
    ],
    resizers: {
      viewer: 'bottom',
    },
    columns: [
      /* Project Nav */
      {
        key: 'directoryNav',
        defaultMin: PROJECT_NAV_MIN_WIDTH,
        defaultSize: MEDIUM_NAV_DEFAULT_WIDTH,
        defaultMax: MEDIUM_NAV_MAX_WIDTH,
        isToggleable: false,
        showing: false,
      },

      /* Projects & Asset Grid */
      {
        key: 'directoryContent',
        defaultMin: 300,
        defaultSize: '1fr',
        isToggleable: false,
        showing: false,
      },

      /* Viewer Panel */
      {
        key: 'viewer',
        defaultMin: 300,
        defaultSize: '1fr',
        isToggleable: false,
        showing: visible.includes('viewer'),
      },

      /* AI Panel (hidden on mobile) */
      {
        key: 'ai',
        defaultMin: AI_DEFAULT_MIN_WIDTH,
        defaultSize: AI_DEFAULT_WIDTH,
        isToggleable: false,
        showing: false,
      },
    ],
    rows: [
      /* MediaViewer Panel */
      {
        key: 'defaultRow',
        defaultMin: VIEWER_ROW_MIN_HEIGHT_BASE,
        defaultSize: '1fr',
        isToggleable: false,
        showing: visible.includes('defaultRow'),
      },

      /* AssetDetails Panel */
      {
        key: 'details',
        defaultMin: DETAILS_ROW_MIN_HEIGHT_BASE,
        defaultSize: DETAILS_DEFAULT_WIDTH,
        isFlex: true,
        isToggleable: true,
        showing: visible.includes('details'),
      },
    ],
  };
}

const sideBySideTemplate = (
  visible: Array<PanelArea>,
  aiColumn: PanelUnitProps
): PanelsBreakpointTemplate => {
  return {
    areas: [['directoryNav', 'directoryContent', 'viewer', 'details', 'ai']],
    resizers: {
      directoryNav: 'right',
      viewer: 'right',
      directoryContent: 'right',
    },
    columns: [
      /* Project Nav */
      {
        key: 'directoryNav',
        defaultMin: PROJECT_NAV_MIN_WIDTH,
        defaultSize: LARGE_NAV_DEFAULT_WIDTH,
        defaultMax: MEDIUM_NAV_MAX_WIDTH,
        isToggleable: false,
        showing: false,
      },

      /* Projects & Asset Grid */
      {
        key: 'directoryContent',
        defaultMin: 300,
        defaultSize: '1fr',
        isToggleable: false,
        showing: false,
      },

      /* MediaViewer Panel */
      {
        key: 'viewer',
        defaultMin: 300,
        defaultSize: '1fr',
        isToggleable: false,
        showing: true,
      },

      /* AssetDetails Panel */
      {
        key: 'details',
        defaultMin: DETAILS_DEFAULT_WIDTH,
        defaultSize: DETAILS_DEFAULT_WIDTH,
        defaultMax: 600,
        isToggleable: true,
        showing: visible.includes('details'),
        panelToggle: {
          variant: 'right',
          label: TEXT.details,
        },
      },

      /* AI Panel */
      aiColumn,
    ],
    rows: [
      {
        key: 'defaultRow',
        defaultMin: 200,
        defaultSize: '1fr',
        isToggleable: false,
      },
    ],
  };
};

/**
 * Medium template
 */
export const mediumTemplate = (
  visible: Array<PanelArea>
): PanelsBreakpointTemplate =>
  sideBySideTemplate(visible, {
    key: 'ai',
    defaultMin: AI_DEFAULT_MIN_WIDTH,
    defaultSize: AI_DEFAULT_WIDTH,
    isToggleable: false,
    showing: false,
  });

/**
 * Large template
 */
export const largeTemplate = (
  visible: Array<PanelArea>
): PanelsBreakpointTemplate =>
  sideBySideTemplate(visible, {
    key: 'ai',
    defaultMin: AI_DEFAULT_MIN_WIDTH,
    defaultSize: AI_DEFAULT_WIDTH,
    isToggleable: true,
    panelToggle: {
      variant: 'right',
      label: TEXT.ai,
    },
    showing: visible.includes('ai'),
  });

export function getPanelState(visible: Array<PanelArea>): PanelsTemplate {
  return {
    base: baseTemplate(visible),
    tablet: mediumTemplate(visible),
    desktop: largeTemplate(visible),
    visible,
  };
}

export const getDefaultVisiblePanels = (): Array<ViewerPanelArea> => {
  return ['viewer', 'defaultRow', 'details'];
};

// Because this page encapsulates three "pages", each has a different layout default
export const getDefaultPanelState = (): PanelsTemplate => {
  return getPanelState(getDefaultVisiblePanels());
};
